import Swal from 'sweetalert2';
import Auth from './auth-token';

const AUTH_HEADER = 'Authorization';

// Публічні маршрути входу та реєстрації. Старий токен зі сховища туди не надсилається:
// прострочений веб-токен давав 401 (EnforceAccessTokenPolicy) ще до самого входу,
// а редирект на /login губив token-login посилання з Telegram-бота.
const PUBLIC_AUTH_PATH = /(^|\/)auth\/(login|telegram\/login|token-login|register|setup-password|email\/verify|email\/resend|send-reset-link-email|password\/reset)(\/|\?|$)/;

export const isPublicAuthRequest = (config) => PUBLIC_AUTH_PATH.test(String((config && config.url) || ''));

const bearerToken = (headers) => {
  const value = headers && (headers[AUTH_HEADER] || headers[AUTH_HEADER.toLowerCase()]);
  return typeof value === 'string' && value.startsWith('Bearer ') ? value.slice(7) : null;
};

// До відправки axios ще тримає заголовки як defaults.common + defaults[method] + власні,
// тому прибирати треба з усіх трьох рівнів.
const stripAuthHeader = (config) => {
  const headers = config.headers;
  const method = String(config.method || 'get').toLowerCase();

  delete headers[AUTH_HEADER];
  delete headers[AUTH_HEADER.toLowerCase()];
  if (headers.common) {
    delete headers.common[AUTH_HEADER];
  }
  if (headers[method]) {
    delete headers[method][AUTH_HEADER];
  }
};

const pending = () => new Promise(() => {});

let isSessionAlertShown = false;
let isAccountSwitchAlertShown = false;

// В іншій вкладці виконано вхід в інший акаунт. Ця вкладка далі працює зі своїм
// токеном (див. Auth.token), а після підтвердження перезавантажується, щоб store,
// форми і Echo-підписки належали тому акаунту, чий токен лежить у сховищі.
export const notifyAccountSwitched = async () => {
  if (isAccountSwitchAlertShown || isSessionAlertShown) {
    return;
  }

  isAccountSwitchAlertShown = true;

  await Swal.fire({
    title: 'Увага!',
    text: 'В іншій вкладці виконано вхід в інший акаунт. Цю сторінку буде оновлено.',
    icon: 'warning',
    confirmButtonText: 'Оновити сторінку',
    confirmButtonColor: '#FB923C',
    allowOutsideClick: false,
    allowEscapeKey: false,
  });

  window.location.reload();
};

// Токен береться перед кожним запитом, а не фіксується при завантаженні вкладки:
// після повторного входу в іншій вкладці ця вкладка одразу шле новий токен, інакше
// вона надсилала б учорашній і ловила 401 (TTL веб-токена або єдина сесія).
// Токен іншого акаунта Auth.token не віддає — тоді лишається власний токен вкладки.
const requestInterceptor = (config) => {
  config.headers = config.headers || {};

  if (isPublicAuthRequest(config)) {
    stripAuthHeader(config);
    return config;
  }

  if (!Auth.isStoredAccountCurrent()) {
    notifyAccountSwitched();
  }

  const token = Auth.token();
  if (token) {
    config.headers[AUTH_HEADER] = 'Bearer ' + token;
  } else {
    stripAuthHeader(config);
  }

  return config;
};

const handleSessionReplaced = async (instance, error) => {
  const response = error.response;
  if (!response || response.status !== 401 || !response.data || response.data.message !== 'session_replaced') {
    return Promise.reject(error);
  }

  // У сховищі інший акаунт: токен цієї вкладки міг застаріти, але спільний токен
  // належить чужій робочій сесії — його не видаляємо і на логін не ведемо,
  // а перезавантажуємо вкладку під акаунт зі сховища.
  if (!Auth.isStoredAccountCurrent()) {
    notifyAccountSwitched();
    return pending();
  }

  // Сервер відхилив токен, але у сховищі вже лежить інший токен того ж акаунта
  // (повторний вхід в іншій вкладці) — тихо повторюємо запит з ним замість діалогу.
  const config = error.config || {};
  const sentToken = bearerToken(config.headers);
  const freshToken = Auth.token();
  if (freshToken && sentToken !== freshToken && !config._retriedWithFreshToken) {
    config._retriedWithFreshToken = true;
    return instance.request(config);
  }

  if (isSessionAlertShown) {
    return pending();
  }

  isSessionAlertShown = true;

  await Swal.fire({
    title: 'Увага!',
    text: response.data.error || 'Ваш акаунт було відкрито на іншому пристрої.',
    icon: 'warning',
    confirmButtonText: 'Виконати вхід повторно',
    confirmButtonColor: '#FB923C',
    allowOutsideClick: false,
    allowEscapeKey: false,
  });

  // Поки діалог був відкритий, в іншій вкладці могли увійти знову: тоді у сховищі
  // вже інший токен. Його не стираємо, а перезавантажуємо вкладку під нову сесію.
  const current = Auth.readStoredSession();
  if (current && current.token !== sentToken) {
    window.location.reload();
    return pending();
  }

  Auth.clearSession();
  window.location.href = '/login';
  return pending();
};

/**
 * Підключає до axios-інстанса підстановку токена і обробку завершення сесії.
 * Викликається для глобального window.axios і для окремого інстанса http-client,
 * щоб запити через будь-який з них однаково повторювались зі свіжим токеном
 * або вели на повторний вхід.
 */
export const attachSessionGuard = (instance) => {
  instance.interceptors.request.use(requestInterceptor);
  instance.interceptors.response.use(
    (response) => response,
    (error) => handleSessionReplaced(instance, error)
  );
};

// Події storage приходять лише з інших вкладок.
export const installSessionStorageListener = () => {
  window.addEventListener('storage', (event) => {
    // Вихід в іншій вкладці: ця вкладка без токена все одно отримає 401 на першому ж
    // запиті, тож ведемо на логін одразу, не чекаючи помилки. Окремий ключ token
    // слухаємо для вкладок зі старою версією кабінету, які пару ще не пишуть.
    const isLogout = (event.key === Auth.SESSION_KEY || event.key === Auth.TOKEN_KEY) && event.oldValue && !event.newValue;
    if (isLogout && !isSessionAlertShown && window.location.pathname !== '/login') {
      isSessionAlertShown = true;
      // Стара вкладка при виході спільний запис не прибирає — інакше після редиректу
      // ця вкладка знову піднялася б із застарілої сесії
      Auth.clearSession();
      window.location.href = '/login';
      return;
    }

    // Вхід у вкладці зі старою версією кабінету: спільний запис перезбирається з окремих
    // ключів. Слухаємо і token, і info: при повторному вході в той самий акаунт info може
    // записатися без змін, а браузер не шле storage для незміненого значення, тож подія
    // прийде лише за token. Стара вкладка пише token першим, тому reconcile відкладається
    // на наступний tick, щоб решта окремих ключів уже були записані.
    const isLegacyLogin = (event.key === Auth.TOKEN_KEY || event.key === Auth.INFO_KEY) && event.newValue;
    if (isLegacyLogin) {
      setTimeout(() => Auth.reconcileLegacySession(), 0);
    }

    // Вхід в інший акаунт без виходу (наприклад, /login?token=... з Telegram-бота).
    if (event.key === Auth.SESSION_KEY && event.oldValue && event.newValue) {
      const before = Auth.parseSession(event.oldValue);
      const after = Auth.parseSession(event.newValue);
      if (before && after && before.keyUser && after.keyUser && before.keyUser !== after.keyUser) {
        notifyAccountSwitched();
      }
    }
  });
};
